import React, { useState, useEffect } from 'react';
import { Menu, X, Image as ImageIcon } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the local logo as default
      const defaultLogo = '/perplexity-new-logo.png';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setIsLoading(false);
          return;
        }

        if (data?.value) {
          // Create new image object to preload
          const img = new Image();
          img.src = data.value;
          
          // Only switch to the new logo once it's fully loaded
          img.onload = () => {
            setLogoUrl(data.value);
            setIsLoading(false);
          };
          
          // Keep default logo if custom logo fails to load
          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setIsLoading(false);
          };
        } else {
          setIsLoading(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error fetching logo:', err);
      setError(err as Error);
      setIsLoading(false);
    }
  };

  const handleConnectClick = () => {
    navigate('/community');
    setIsOpen(false);
  };

  const scrollToProducts = () => {
    if (location.pathname !== '/') {
      navigate('/#products');
    } else {
      const productsSection = document.querySelector('#products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const isCommunityPage = location.pathname === '/community';

  return (
    <nav className={`fixed w-full z-50 ${isCommunityPage ? 'bg-black' : 'bg-black/80 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <div className="flex-1 flex items-center justify-between">
            <Link to="/" className="text-white hover:text-primary transition-colors">
              {isLoading ? (
                <div className="h-32 w-[300px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <>
                  <img
                    src={logoUrl}
                    alt="Perplexity"
                    className="h-32 w-auto object-contain"
                    onError={(e) => {
                      console.warn('Error loading logo image, falling back to text');
                      e.currentTarget.style.display = 'none';
                      const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (textFallback) {
                        textFallback.style.display = 'block';
                      }
                    }}
                  />
                  <div
                    className="h-32 flex items-center justify-center text-primary text-2xl font-bold"
                    style={{ display: 'none' }}
                  >
                    PERPLEXITY
                  </div>
                </>
              ) : (
                <div className="h-32 w-[300px] flex items-center justify-center text-primary">
                  <ImageIcon size={32} />
                </div>
              )}
            </Link>
            
            <div className="hidden md:flex items-center space-x-4">
              {!isCommunityPage && (
                <button
                  onClick={handleConnectClick}
                  className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105"
                >
                  Community Connect
                </button>
              )}
              
              <div className="relative group">
                <button className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105">
                  Signup
                  <svg className="w-4 h-4 ml-2 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className="absolute right-0 top-full pt-2 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-black/90 backdrop-blur-md rounded-lg shadow-xl border border-gray-700 py-2">
                    <Link
                      to="/pricing"
                      className="block px-4 py-3 text-white hover:bg-primary/20 hover:text-primary transition-colors border-b border-gray-700"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="font-medium">Workday Services Customer</div>
                      <div className="text-sm text-gray-400">View plans &amp; pricing</div>
                    </Link>

                    <Link
                      to="/customer-signup?type=consultant"
                      className="block px-4 py-3 text-white hover:bg-primary/20 hover:text-primary transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="font-medium">Workday Consultants &amp; Professionals</div>
                      <div className="text-sm text-gray-400">Create account for access</div>
                    </Link>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <button className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105">
                  Login
                  <svg className="w-4 h-4 ml-2 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className="absolute right-0 top-full pt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-black/90 backdrop-blur-md rounded-lg shadow-xl border border-gray-700 py-2">
                    <Link
                      to="/customer/login"
                      className="block px-4 py-3 text-white hover:bg-primary/20 hover:text-primary transition-colors border-b border-gray-700"
                     onClick={() => setIsOpen(false)}
                    >
                      <div className="font-medium">Workday Services Customers</div>
                      <div className="text-sm text-gray-400">Access your client account</div>
                    </Link>

                    <Link
                      to="/consultant/login"
                      className="block px-4 py-3 text-white hover:bg-primary/20 hover:text-primary transition-colors"
                     onClick={() => setIsOpen(false)}
                    >
                      <div className="font-medium">Workday Consultants and Professionals</div>
                      <div className="text-sm text-gray-400">Access your account</div>
                    </Link>

                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-300">
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-4 sm:px-3 bg-black flex flex-col items-center">
            {!isCommunityPage && (
              <button
                onClick={handleConnectClick}
                className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105 w-full text-center justify-center"
              >
                Community Connect
              </button>
            )}
            
            {/* Mobile Signup Options */}
            <div className="w-full space-y-2">
              <div className="text-white text-sm font-medium text-center mb-2">Signup:</div>
              <Link
                to="/pricing"
                className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg flex flex-col items-center hover:bg-black/70 transition-all transform hover:scale-105 w-full text-center"
                onClick={() => setIsOpen(false)}
              >
                <span>Workday Services Customer</span>
                <span className="text-xs text-gray-400">View plans &amp; pricing</span>
              </Link>
              <Link
                to="/customer-signup?type=consultant"
                className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg flex flex-col items-center hover:bg-black/70 transition-all transform hover:scale-105 w-full text-center"
                onClick={() => setIsOpen(false)}
              >
                <span>Workday Consultants &amp; Professionals</span>
                <span className="text-xs text-gray-400">Create account for access</span>
              </Link>
            </div>

            {/* Mobile Login Options */}
            <div className="w-full space-y-2">
              <div className="text-white text-sm font-medium text-center mb-2">Login Options:</div>
              <Link
                to="/customer/login"
                className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105 w-full text-center justify-center"
               onClick={() => setIsOpen(false)}
              >
                Workday Services Customers
              </Link>
              <Link
                to="/consultant/login"
                className="gradient-border bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-black/70 transition-all transform hover:scale-105 w-full text-center justify-center"
               onClick={() => setIsOpen(false)}
              >
                Workday Consultants and Professionals
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}